import type { Template, FieldDefinition, BlogPost } from '../types'

const API_BASE = '/api'

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('admin_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function isTokenValid(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token))
    return payload.exp > Date.now()
  } catch {
    return false
  }
}

function handleUnauthorized() {
  localStorage.removeItem('admin_token')
  window.location.href = '/login'
}

async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem('admin_token')
  
  // Check if token is expired before making request
  if (token && !isTokenValid(token)) {
    handleUnauthorized()
    throw new Error('Token expired')
  }
  
  const response = await fetch(url, options)
  
  // Handle 401 responses
  if (response.status === 401) {
    handleUnauthorized()
    throw new Error('Unauthorized')
  }
  
  return response
}

export async function fetchTemplates(): Promise<Template[]> {
  const response = await fetch(`${API_BASE}/templates`)
  if (!response.ok) throw new Error('Failed to fetch templates')
  return response.json()
}

export async function fetchTemplate(id: string): Promise<Template> {
  const response = await fetch(`${API_BASE}/templates/${id}`)
  if (!response.ok) throw new Error('Failed to fetch template')
  return response.json()
}

export async function createTemplate(template: {
  name: string
  description: string | null
  category: string
  content: string
  fields: FieldDefinition[]
}): Promise<Template> {
  const response = await fetchWithAuth(`${API_BASE}/templates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(template),
  })
  if (!response.ok) throw new Error('Failed to create template')
  return response.json()
}

export async function updateTemplate(id: string, template: {
  name?: string
  description?: string | null
  category?: string
  content?: string
  fields?: FieldDefinition[]
}): Promise<Template> {
  const response = await fetchWithAuth(`${API_BASE}/templates/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(template),
  })
  if (!response.ok) throw new Error('Failed to update template')
  return response.json()
}

export async function deleteTemplate(id: string): Promise<void> {
  const response = await fetchWithAuth(`${API_BASE}/templates/${id}`, {
    method: 'DELETE',
    headers: { ...getAuthHeaders() },
  })
  if (!response.ok) throw new Error('Failed to delete template')
}

export async function uploadToR2(pdfBlob: Blob, filename: string): Promise<{ key: string; url: string }> {
  const formData = new FormData()
  formData.append('file', pdfBlob, filename)

  const response = await fetch(`${API_BASE}/upload`, {
    method: 'POST',
    body: formData,
  })
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { details?: string; error?: string }
    const errorMessage = errorData.details || errorData.error || 'Failed to upload file'
    throw new Error(errorMessage)
  }
  
  return response.json()
}

// Blog API
export async function fetchBlogPosts(includeDisabled = false): Promise<BlogPost[]> {
  const url = includeDisabled ? `${API_BASE}/blog?all=true` : `${API_BASE}/blog`
  const response = await fetch(url, {
    headers: includeDisabled ? { ...getAuthHeaders() } : {},
  })
  if (!response.ok) throw new Error('Failed to fetch blog posts')
  return response.json()
}

export async function fetchBlogPost(slug: string): Promise<BlogPost> {
  const response = await fetch(`${API_BASE}/blog/${slug}`)
  if (!response.ok) throw new Error('Failed to fetch blog post')
  return response.json()
}

export async function createBlogPost(post: Omit<BlogPost, 'id' | 'created_at' | 'updated_at'>): Promise<BlogPost> {
  const response = await fetchWithAuth(`${API_BASE}/blog`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(post),
  })
  if (!response.ok) throw new Error('Failed to create blog post')
  return response.json()
}

export async function updateBlogPost(id: string, post: Partial<BlogPost>): Promise<BlogPost> {
  const response = await fetchWithAuth(`${API_BASE}/blog/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(post),
  })
  if (!response.ok) throw new Error('Failed to update blog post')
  return response.json()
}

export async function deleteBlogPost(id: string): Promise<void> {
  const response = await fetchWithAuth(`${API_BASE}/blog/${id}`, {
    method: 'DELETE',
    headers: { ...getAuthHeaders() },
  })
  if (!response.ok) throw new Error('Failed to delete blog post')
}

// Turnstile verification
export async function verifyTurnstile(token: string, action: string): Promise<boolean> {
  const response = await fetch(`${API_BASE}/verify-turnstile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, action }),
  })
  if (!response.ok) return false
  const data = await response.json() as { success: boolean }
  return data.success === true
}

export async function uploadToR2WithTurnstile(
  pdfBlob: Blob, 
  filename: string, 
  turnstileToken: string
): Promise<{ key: string; url: string }> {
  const formData = new FormData()
  formData.append('file', pdfBlob, filename)
  formData.append('turnstile_token', turnstileToken)

  const response = await fetch(`${API_BASE}/upload`, {
    method: 'POST',
    body: formData,
  })
  if (!response.ok) throw new Error('Failed to upload file')
  return response.json()
}

// Statistics API
export async function recordStat(action: string, templateId?: string): Promise<void> {
  try {
    await fetch(`${API_BASE}/stats`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, template_id: templateId }),
    })
  } catch {
    // Silently fail - stats are not critical
  }
}

export interface Statistics {
  period: { days: number; start_date: string }
  totals: Record<string, number>
  today: Record<string, number>
  daily: Array<{ date: string; action: string; total: number }>
  by_template: Array<{ template_id: string; template_name: string | null; action: string; total: number }>
}

export async function fetchStats(days = 30): Promise<Statistics> {
  const response = await fetchWithAuth(`${API_BASE}/stats?days=${days}`, {
    headers: { ...getAuthHeaders() },
  })
  if (!response.ok) throw new Error('Failed to fetch stats')
  return response.json()
}

// Autocomplete API
export interface AutocompleteItem {
  id: string
  name: string
  address?: string
  nif?: string
  [key: string]: string | undefined
}

export interface AutocompleteListItem {
  id: string
  field_name: string
  name: string
  data: string
  sort_order: number
  created_at: string
  updated_at: string
}

export async function fetchAutocompleteOptions(fieldName: string): Promise<AutocompleteItem[]> {
  const response = await fetch(`${API_BASE}/autocomplete/${encodeURIComponent(fieldName)}`)
  if (!response.ok) return []
  return response.json()
}

export async function fetchAutocompleteLists(): Promise<{ lists: Record<string, AutocompleteListItem[]>; items: AutocompleteListItem[] }> {
  const response = await fetchWithAuth(`${API_BASE}/autocomplete-lists`, {
    headers: { ...getAuthHeaders() },
  })
  if (!response.ok) throw new Error('Failed to fetch autocomplete lists')
  return response.json()
}

export async function createAutocompleteItem(fieldName: string, name: string, data: Record<string, string>, sortOrder = 0): Promise<AutocompleteItem> {
  const response = await fetchWithAuth(`${API_BASE}/autocomplete-lists`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ field_name: fieldName, name, data, sort_order: sortOrder }),
  })
  if (!response.ok) throw new Error('Failed to create item')
  return response.json()
}

export async function updateAutocompleteItem(id: string, updates: { name?: string; data?: Record<string, string>; sort_order?: number }): Promise<void> {
  const response = await fetchWithAuth(`${API_BASE}/autocomplete-lists/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(updates),
  })
  if (!response.ok) throw new Error('Failed to update item')
}

export async function deleteAutocompleteItem(id: string): Promise<void> {
  const response = await fetchWithAuth(`${API_BASE}/autocomplete-lists/${id}`, {
    method: 'DELETE',
    headers: { ...getAuthHeaders() },
  })
  if (!response.ok) throw new Error('Failed to delete item')
}

// Change password
export async function changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message?: string; error?: string }> {
  const response = await fetchWithAuth(`${API_BASE}/auth/change-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  })
  const data = await response.json() as { success?: boolean; message?: string; error?: string }
  if (!response.ok) {
    throw new Error(data.error || 'Failed to change password')
  }
  return { success: true, message: data.message }
}
